import { useColorMap, useEventTypes } from '../../hooks/queries/useEventTypes';
import type { EventType, EventTypeFamily } from '../../services/calendar/eventTypeService';
import { cn } from '../../lib/cn';

/** Choix du type d'une audience ou d'un rendez-vous : une pastille par type, à sa couleur. */
export function EventTypeSelect({
  family,
  value,
  onChange,
  disabled,
  className,
}: {
  family: EventTypeFamily;
  value: string | null;
  /** Second argument : le type choisi, null quand on le retire. */
  onChange: (id: string | null, type: EventType | null) => void;
  disabled?: boolean;
  className?: string;
}) {
  const { data: types = [] } = useEventTypes();
  const colors = useColorMap();
  const options = types.filter((t) => t.family === family && (t.is_active || t.id === value));

  if (options.length === 0) return null;

  return (
    <div className={cn('flex flex-wrap gap-1.5', className)} role="radiogroup" aria-label="Type">
      {options.map((type) => {
        const selected = type.id === value;
        const color = colors[type.id] ?? type.color;
        return (
          <button
            key={type.id}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={disabled}
            onClick={() => (selected ? onChange(null, null) : onChange(type.id, type))}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors disabled:opacity-60',
              selected
                ? 'border-transparent font-semibold text-cime-text-primary'
                : 'border-neutral-200 bg-white text-cime-text-muted hover:border-neutral-300 hover:text-cime-text-primary',
            )}
            style={selected ? { background: `color-mix(in srgb, ${color} 22%, white)`, boxShadow: `inset 0 0 0 1.5px ${color}` } : undefined}
          >
            <span className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ background: color }} aria-hidden />
            {type.name}
          </button>
        );
      })}
    </div>
  );
}
